import { toRichHtml } from '../../components/RichText';
import { sanitizeCommentHtml } from '../comments/commentHtml';
import type { HuddleComment } from './huddleApi';

export function huddleCommentTime(value: string, now = new Date()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const sameDay = date.toDateString() === now.toDateString();
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleString('en-US', {
    ...(sameDay ? {} : { month: 'short', day: 'numeric' }),
    ...(sameYear ? { hour: 'numeric', minute: '2-digit', hour12: true } : { year: 'numeric' }),
  });
}

/** The public payload exposes author name and avatar, not a profile id. */
export function groupHuddleComments(comments: HuddleComment[]): HuddleComment[][] {
  const groups: HuddleComment[][] = [];
  for (const comment of comments) {
    const group = groups.at(-1);
    const previous = group?.at(-1);
    const gap = previous ? Date.parse(comment.created_at) - Date.parse(previous.created_at) : NaN;
    if (
      previous &&
      previous.author === comment.author &&
      previous.author_avatar_url === comment.author_avatar_url &&
      gap >= 0 &&
      gap <= 10 * 60_000
    ) {
      group?.push(comment);
    } else {
      groups.push([comment]);
    }
  }
  return groups;
}

/**
 * TAP Console shows the newest comment on top (Kyu, 30 Sept). Groups by the
 * same rule, oldest first as read_huddle sends them, then turns both the
 * group order and the entries inside each group.
 */
export function newestFirstGroups(comments: HuddleComment[]): HuddleComment[][] {
  return groupHuddleComments(comments)
    .reverse()
    .map((group) => group.reverse());
}

export function huddleLinkLabel(href: string): string | null {
  try {
    const url = new URL(href);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (url.hostname === 'docs.google.com') {
      if (/^\/document(?:\/|$)/.test(url.pathname)) return 'Google Doc';
      if (/^\/spreadsheets(?:\/|$)/.test(url.pathname)) return 'Google Sheet';
      if (/^\/presentation(?:\/|$)/.test(url.pathname)) return 'Google Slides';
    }
    return url.hostname === 'drive.google.com' ? 'Drive file' : url.hostname;
  } catch {
    return null;
  }
}

/** Decorate only the Hub sanitizer's output. Never loosen its URL/image rules. */
export function huddleCommentHtml(body: string): string {
  const holder = document.createElement('template');
  holder.innerHTML = sanitizeCommentHtml(/<[a-z][^>]*>/i.test(body) ? body : toRichHtml(body));

  // Old plain-text comments may contain an unlinked URL. Recognize a bare
  // URL text node without rewriting prose, code, or a link's chosen label.
  const walker = document.createTreeWalker(holder.content, NodeFilter.SHOW_TEXT);
  const bare: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (
      !node.parentElement?.closest('a, pre, code') &&
      /^https?:\/\/\S+$/.test(node.data.trim()) &&
      huddleLinkLabel(node.data.trim())
    )
      bare.push(node);
  }
  for (const node of bare) {
    const link = document.createElement('a');
    link.setAttribute('href', node.data.trim());
    link.textContent = node.data.trim();
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    node.replaceWith(link);
  }
  for (const link of holder.content.querySelectorAll('a[href]')) {
    const href = link.getAttribute('href') ?? '';
    const label = huddleLinkLabel(href);
    if (!label || link.textContent?.trim() !== href || link.children.length > 0) continue;
    link.className = 'huddle-link-chip';
    link.setAttribute('title', href);
    link.textContent = '';
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '↗';
    link.append(icon, document.createTextNode(label));
  }
  for (const image of holder.content.querySelectorAll('img')) {
    // Image hosts have already been checked by sanitizeCommentHtml.
    const link = document.createElement('a');
    link.href = image.getAttribute('src') ?? '';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.className = 'huddle-image-link';
    // The link is only the fallback if scripts fail; the viewer takes the tap.
    link.setAttribute('data-image-viewer', '');
    link.setAttribute('aria-label', `Open full-size ${image.alt || 'image'}`);
    // Avoid nested anchors when a saved image was already a link.
    const existing = image.closest('a');
    if (existing) {
      existing.replaceWith(...existing.childNodes);
    }
    image.replaceWith(link);
    link.append(image);
  }
  return holder.innerHTML;
}
